import { useState, useEffect, useMemo } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const POR_PAGINA = 50;

const authFetch = (url) =>
  fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// ── Colores por estado ────────────────────────────────────────────────────────
function colorEstado(estado = "") {
  const e = estado.toUpperCase();
  if (e.includes("ENTREGADO"))                         return { bg: "#ECFDF5", color: "#059669" };
  if (e.includes("DEVOLUCION") || e.includes("RECHAZ")) return { bg: "#FEF2F2", color: "#DC2626" };
  if (e.includes("CANCELADO"))                         return { bg: "#FEF2F2", color: "#DC2626" };
  if (e.includes("TRANSITO") || e.includes("TRÁNSITO")) return { bg: "#EFF6FF", color: "#2563EB" };
  if (e.includes("PENDIENTE"))                         return { bg: "#FFFBEB", color: "#D97706" };
  if (e.includes("CONFIRMADO"))                        return { bg: "#F0FDF4", color: "#16A34A" };
  return { bg: "#F3F4F6", color: "#6B7280" };
}

// ── Cabecera de columna ordenable ─────────────────────────────────────────────
function Th({ children, campo, ordenPor, dir, onOrdenar, style = {} }) {
  const activo = ordenPor === campo;
  return (
    <th
      onClick={() => onOrdenar(campo)}
      style={{
        padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 700,
        color: activo ? "#059669" : "#6B7280", whiteSpace: "nowrap",
        cursor: "pointer", userSelect: "none", borderBottom: "2px solid #E5E7EB",
        background: "#F9FAFB", position: "sticky", top: 0, zIndex: 1,
        ...style,
      }}
    >
      {children}{" "}
      <span style={{ opacity: activo ? 1 : 0.3, fontSize: 10 }}>
        {activo ? (dir === "asc" ? "▲" : "▼") : "▲▼"}
      </span>
    </th>
  );
}

export default function TablaPedidos() {
  const [pedidos,   setPedidos]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [busqueda,  setBusqueda]  = useState("");
  const [ordenPor,  setOrdenPor]  = useState("fecha");
  const [dir,       setDir]       = useState("desc");
  const [pagina,    setPagina]    = useState(1);

  useEffect(() => {
    setLoading(true);
    authFetch(`${API_URL}/api/tabla-pedidos`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => { setPedidos(data.pedidos || []); setLoading(false); })
      .catch(() => { setError("No se pudieron cargar los pedidos."); setLoading(false); });
  }, []);

  // Filtrar por búsqueda (ID, producto, guía)
  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return pedidos;
    const q = busqueda.trim().toLowerCase();
    return pedidos.filter(p =>
      (p.id       || "").toLowerCase().includes(q) ||
      (p.producto || "").toLowerCase().includes(q) ||
      (p.guia     || "").toLowerCase().includes(q)
    );
  }, [pedidos, busqueda]);

  // Ordenar
  const ordenados = useMemo(() => {
    return [...filtrados].sort((a, b) => {
      let va = a[ordenPor] ?? "";
      let vb = b[ordenPor] ?? "";
      if (typeof va === "number") return dir === "asc" ? va - vb : vb - va;
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      return dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [filtrados, ordenPor, dir]);

  // Paginación
  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / POR_PAGINA));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visibles = ordenados.slice((paginaSegura - 1) * POR_PAGINA, paginaSegura * POR_PAGINA);

  const handleOrdenar = (campo) => {
    if (ordenPor === campo) setDir(d => d === "asc" ? "desc" : "asc");
    else { setOrdenPor(campo); setDir("asc"); }
    setPagina(1);
  };

  const handleBusqueda = (v) => { setBusqueda(v); setPagina(1); };

  // Detectar qué columnas tiene el archivo
  const cols = pedidos.length > 0 ? Object.keys(pedidos[0]) : [];
  const tiene = (k) => cols.includes(k);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, gap: 12, color: "#6B7280" }}>
      <div style={{ width: 22, height: 22, border: "2px solid #E5E7EB", borderTop: "2px solid #059669", borderRadius: "50%", animation: "spin 0.8s linear infinite" }}/>
      Cargando pedidos...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: 40, textAlign: "center", color: "#DC2626", fontSize: 14 }}>{error}</div>
  );

  if (!pedidos.length) return (
    <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontSize: 14 }}>
      No hay pedidos cargados. Subí tu archivo en la sección <strong>Archivos</strong>.
    </div>
  );

  return (
    <div style={{ fontFamily: "'Space Grotesk', -apple-system, sans-serif" }}>

      {/* Barra superior */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {/* Buscador */}
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: "#9CA3AF" }}>🔍</span>
          <input
            value={busqueda}
            onChange={e => handleBusqueda(e.target.value)}
            placeholder="Buscar por N° pedido, producto o guía..."
            style={{
              width: "100%", padding: "10px 12px 10px 36px", borderRadius: 10,
              border: "1.5px solid #E5E7EB", fontSize: 14, outline: "none",
              fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Contador */}
        <div style={{ fontSize: 13, color: "#6B7280", whiteSpace: "nowrap" }}>
          {busqueda ? (
            <><strong style={{ color: "#111827" }}>{filtrados.length}</strong> de {pedidos.length} pedidos</>
          ) : (
            <><strong style={{ color: "#111827" }}>{pedidos.length}</strong> pedidos en total</>
          )}
        </div>
      </div>

      {/* Tabla con scroll horizontal */}
      <div style={{ overflowX: "auto", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {tiene("id")            && <Th campo="id"            ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>N° Pedido</Th>}
              {tiene("fecha")         && <Th campo="fecha"         ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>Fecha</Th>}
              {tiene("producto")      && <Th campo="producto"      ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar} style={{ minWidth: 160 }}>Producto</Th>}
              {tiene("estado")        && <Th campo="estado"        ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>Estado</Th>}
              {tiene("transportadora")&& <Th campo="transportadora"ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>Transportadora</Th>}
              {tiene("guia")          && <Th campo="guia"          ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>Guía</Th>}
              {tiene("cliente")       && <Th campo="cliente"       ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar} style={{ minWidth: 140 }}>Cliente</Th>}
              {tiene("departamento")  && <Th campo="departamento"  ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>Departamento</Th>}
              {tiene("total")         && <Th campo="total"         ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>Total</Th>}
              {tiene("flete")         && <Th campo="flete"         ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar}>Flete</Th>}
              {tiene("novedad")       && <Th campo="novedad"       ordenPor={ordenPor} dir={dir} onOrdenar={handleOrdenar} style={{ minWidth: 180 }}>Novedad</Th>}
            </tr>
          </thead>
          <tbody>
            {visibles.map((p, i) => {
              const { bg, color } = colorEstado(p.estado);
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? "#FFFFFF" : "#FAFAFA", borderBottom: "1px solid #F3F4F6" }}>
                  {tiene("id")            && <td style={{ padding: "9px 12px", fontFamily: "monospace", color: "#374151", fontWeight: 600 }}>{p.id}</td>}
                  {tiene("fecha")         && <td style={{ padding: "9px 12px", color: "#6B7280", whiteSpace: "nowrap" }}>{p.fecha}</td>}
                  {tiene("producto")      && <td style={{ padding: "9px 12px", color: "#111827", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.producto}>{p.producto}</td>}
                  {tiene("estado")        && (
                    <td style={{ padding: "9px 12px" }}>
                      <span style={{ background: bg, color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                        {p.estado}
                      </span>
                    </td>
                  )}
                  {tiene("transportadora")&& <td style={{ padding: "9px 12px", color: "#374151" }}>{p.transportadora}</td>}
                  {tiene("guia")          && <td style={{ padding: "9px 12px", fontFamily: "monospace", color: "#374151" }}>{p.guia}</td>}
                  {tiene("cliente")       && <td style={{ padding: "9px 12px", color: "#374151", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.cliente}>{p.cliente}</td>}
                  {tiene("departamento")  && <td style={{ padding: "9px 12px", color: "#6B7280" }}>{p.departamento}</td>}
                  {tiene("total")         && <td style={{ padding: "9px 12px", color: "#111827", fontWeight: 600, textAlign: "right" }}>${Number(p.total).toLocaleString("es-CL")}</td>}
                  {tiene("flete")         && <td style={{ padding: "9px 12px", color: "#6B7280", textAlign: "right" }}>${Number(p.flete).toLocaleString("es-CL")}</td>}
                  {tiene("novedad")       && <td style={{ padding: "9px 12px", color: "#6B7280", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.novedad}>{p.novedad}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
          <button
            onClick={() => setPagina(p => Math.max(1, p - 1))}
            disabled={paginaSegura === 1}
            style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #E5E7EB", background: paginaSegura === 1 ? "#F9FAFB" : "#fff", cursor: paginaSegura === 1 ? "default" : "pointer", fontSize: 13, color: "#374151" }}
          >
            ← Anterior
          </button>

          <span style={{ fontSize: 13, color: "#6B7280" }}>
            Página <strong style={{ color: "#111827" }}>{paginaSegura}</strong> de <strong style={{ color: "#111827" }}>{totalPaginas}</strong>
          </span>

          <button
            onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
            disabled={paginaSegura === totalPaginas}
            style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #E5E7EB", background: paginaSegura === totalPaginas ? "#F9FAFB" : "#fff", cursor: paginaSegura === totalPaginas ? "default" : "pointer", fontSize: 13, color: "#374151" }}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}
